import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import labService from './labService.js';
import authService from '../auth/authService';
import './css/Lab.css';
import './css/DefineNanoform.css';
import Visualization from './Visualization.js';
import LoadingScreen from './LoadingScreen';

// Import SVG images
import sphericalIconSVG from './images/legacy_shapes/spherical.svg';
import cubicIconSVG from './images/legacy_shapes/cubic.svg';
import polyhedralIconSVG from './images/legacy_shapes/polyhedral.svg';
import rodIconSVG from './images/legacy_shapes/rod.svg';
import tubeIconSVG from './images/legacy_shapes/tube.svg';
import wiresIconSVG from './images/legacy_shapes/wires.svg';
import plateletIconSVG from './images/legacy_shapes/platelet.svg';
import inconclusiveIconSVG from './images/legacy_shapes/inconclusive.svg';

const DefineNanoform = () => {
  const navigate = useNavigate();

  // Stany walidacji i ładowania
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Stany dla danych z backendu
  const [modelDescriptors, setModelDescriptors] = useState(null);
  const [endpointDetails, setEndpointDetails] = useState(null);

  // Stany wprowadzanych danych
  const [visualizationType, setVisualizationType] = useState('inconclusive');
  const [descriptorValues, setDescriptorValues] = useState({});
  const [selectedCrystalStructure, setSelectedCrystalStructure] = useState('');
  const [meanParticleSize, setMeanParticleSize] = useState("");

  // States for Core Modification & Surface (Nowości w UI)
  const [isCrystalOpen, setIsCrystalOpen] = useState(false);

  const [isCoreOpen, setIsCoreOpen] = useState(false);
  const [selectedCore, setSelectedCore] = useState('No modification');

  const [isSurfaceOpen, setIsSurfaceOpen] = useState(false);
  const [selectedSurface, setSelectedSurface] = useState('No modification');

  const coreOptions = ['No modification', 'Ag', 'Au', 'Pt', 'Other'];
  const surfaceOptions = ['No modification', 'PEG', 'PDA', 'Plasma', 'Other'];

  // Shape configuration
  const shapeConfig = {
    spherical: { icon: sphericalIconSVG },
    cubic: { icon: cubicIconSVG },
    polyhedral: { icon: polyhedralIconSVG },
    rod: { icon: rodIconSVG },
    tube: { icon: tubeIconSVG },
    wires: { icon: wiresIconSVG },
    platelet: { icon: plateletIconSVG },
    inconclusive: { icon: inconclusiveIconSVG }
  };

  const closeSelectModels = () => {
    // Użytkownik wychodzi z kreatora ("X") - czyścimy wszystko, co związane z analizą
    localStorage.removeItem('selectedEndpoint');
    localStorage.removeItem('analysisName');
    localStorage.removeItem('selectedModels');
    localStorage.removeItem('descriptorValues');
    navigate('/laboratory');
  };

  const handleBack = () => {
    // Wracamy do kroku 2 - czyścimy tylko wpisane deskryptory, żeby były puste na przyszłość,
    // ale zostawiamy modele w pamięci, żeby zaznaczenia w kroku 2 nie zniknęły
    localStorage.removeItem('descriptorValues');
    navigate('/define-endpoints');
  };

  // --- POBIERANIE DANYCH ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = authService.getCurrentUser()?.access;
        const selectedModelsString = localStorage.getItem('selectedModels');
        const savedEndpointId = localStorage.getItem('selectedEndpoint');

        if (!token) {
          setError("You must be logged in to view this page.");
          setLoading(false);
          return;
        }

        if (!selectedModelsString || !savedEndpointId) {
          // Wyrzucamy na początek, jeśli brakuje danych w localStorage
          navigate('/choose-substance');
          return;
        }

        const selectedModelsObj = JSON.parse(selectedModelsString);
        const modelIds = Object.keys(selectedModelsObj).map(id => parseInt(id));
        const endpointId = JSON.parse(savedEndpointId);

        // Pobieramy informacje o endpointcie (do nazwy w wizualizacji) i deskryptorach jednocześnie
        const [endpointRes, descriptorsRes] = await Promise.all([
          labService.getEndpointDetail(endpointId, token),
          labService.getModelDescriptors(token, modelIds)
        ]);

        setEndpointDetails(endpointRes.data);

        const data = descriptorsRes.data;
        setModelDescriptors(data);

        // Ustaw domyślną strukturę krystaliczną
        if (data.crystal_structure?.available_structures?.length > 0) {
          const defaultStructure = data.crystal_structure.default || data.crystal_structure.available_structures[0];
          setSelectedCrystalStructure(defaultStructure);
        }

        // USUNIĘTO: Odczytywanie starych deskryptorów z localStorage. Zawsze startujemy od nowa.

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load form requirements. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // --- OBSŁUGA FORMULARZA ---
  const handleDescriptorChange = (frontendKey, value) => {
    setDescriptorValues(prev => ({
      ...prev,
      [frontendKey]: value
    }));
  };

  const validateDescriptors = () => {
    if (!modelDescriptors || !modelDescriptors.descriptors2frontend || modelDescriptors.descriptors2frontend.length === 0) {
      return true;
    }

    for (let i = 0; i < modelDescriptors.descriptors2frontend.length; i++) {
      const key = modelDescriptors.descriptors2frontend[i];
      const value = descriptorValues[key];

      if (!value || value.trim() === '') {
        return false;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        return false;
      }
    }
    return true;
  };

  const validateCrystalStructure = () => {
    if (modelDescriptors?.crystal_structure?.available_structures?.length > 0) {
      return selectedCrystalStructure !== '';
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setError(null);

    if (!validateDescriptors()) {
      setError("Please fill all required descriptor fields with valid positive numbers.");
      window.scrollTo(0, 0);
      return;
    }

    if (!validateCrystalStructure()) {
      setError("Please select a crystal structure.");
      window.scrollTo(0, 0);
      return;
    }

    const token = authService.getCurrentUser()?.access;

    // --- KLUCZOWA ZMIANA: FORMATOWANIE MODELI DLA BACKENDU ---
    const rawSelectedModels = JSON.parse(localStorage.getItem('selectedModels')) || {};
    const formattedSelectedModels = {};

    Object.keys(rawSelectedModels).forEach(key => {
      // Upewniamy się, że wartość jest tablicą
      formattedSelectedModels[key] = Array.isArray(rawSelectedModels[key]) ? rawSelectedModels[key] : [];
      // Zabezpieczenie: jeśli tablica jest pusta, dodajemy "default", 
      // żeby len() w Pythonie wyniosło 1 (unikamy mnożenia przez zero przy liczeniu tokenów)
      if (formattedSelectedModels[key].length === 0) {
        formattedSelectedModels[key] = ["default"];
      }
    });
    // ----------------------------------------------------------

    const endpoint_type = JSON.parse(localStorage.getItem('selectedEndpoint'));
    const anal_name = JSON.parse(localStorage.getItem('analysisName')) || 'Analysis';

    // USUNIĘTO: Zapisywanie deskryptorów do localStorage

    const data = {
      name: anal_name,
      visualization_type: visualizationType,
      descriptors: descriptorValues,
      endpoint_type: endpoint_type,
      // Używamy naszych poprawnie sformatowanych modeli:
      selected_models: formattedSelectedModels,
      selected_crystal_structure: selectedCrystalStructure || null,
      core_modification: selectedCore === 'No modification' ? null : selectedCore,
      surface_functionalization: selectedSurface === 'No modification' ? null : selectedSurface,
    };

    try {
      setLoading(true);

      console.log("1. Wysyłam dane do createAnalysis:", data);

      // 1. Zapisujemy analizę w bazie
      const response = await labService.createAnalysis(data, token);
      const analysisId = response?.data?.analysis_id;

      if (analysisId) {
        console.log("Analiza utworzona poprawnie! ID:", analysisId);

        // 2. Od razu po utworzeniu, zlecamy backendowi start obliczeń!
        const calcResponse = await labService.runCalculation(analysisId, token);
        console.log("Obliczenia wystartowały:", calcResponse.data);

        // SUKCES: Czyścimy wszystko, co trzymaliśmy na czas tworzenia predykcji
        localStorage.removeItem('selectedEndpoint');
        localStorage.removeItem('analysisName');
        localStorage.removeItem('selectedModels');
        localStorage.removeItem('descriptorValues');

        // 3. Przekierowujemy do ekranu ładowania
        navigate(`/calculating-prediction/${analysisId}`);
      } else {
        setError("Failed to generate analysis ID.");
        setLoading(false);
      }
    } catch (error) {
      console.error("=== BŁĄD API ===");
      console.error(error);
      if (error.response) {
        console.error("Dane błędu z Django:", error.response.data);
        const detailMsg = error.response.data.detail || JSON.stringify(error.response.data);
        setError(`Backend Error: ${detailMsg}`);
      } else {
        setError("An error occurred while creating or starting the analysis. Please try again.");
      }
      setLoading(false);
    }
  };

  // --- RENDEROWANIE KOMPONENTÓW ---
  const renderShapeButton = (shape, displayName = null) => {
    const config = shapeConfig[shape];
    const isActive = visualizationType === shape;
    const name = displayName || shape.charAt(0).toUpperCase() + shape.slice(1);

    return (
      <div className="input-wrapper" key={shape}>
        <label className="css-0" style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name="shape-radio-group"
            value={name}
            checked={isActive}
            onChange={() => setVisualizationType(shape)}
            style={{
              border: '0px', clip: 'rect(0px, 0px, 0px, 0px)', height: '1px', width: '1px',
              margin: '-1px', padding: '0px', overflow: 'hidden', position: 'absolute'
            }}
          />
          <div
            aria-hidden="true"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '8px 16px',
              background: isActive ? 'linear-gradient(rgb(20, 150, 69) 0%, rgb(116, 175, 22) 100%)' : '#f4f4f4',
              color: isActive ? 'white' : '#1b4581',
              border: '1px solid #e3e2e2', borderRadius: '0.35rem',
              fontWeight: isActive ? 'bold' : '600', minWidth: '130px', transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img
                src={config.icon}
                alt={`${name} shape`}
                style={{
                  width: '20px', height: '20px',
                  filter: isActive ? 'brightness(0) invert(1)' : 'invert(21%) sepia(30%) saturate(3645%) hue-rotate(195deg) brightness(92%) contrast(85%)'
                }}
              />
              {name}
            </div>
          </div>
        </label>
      </div>
    );
  };

  // Helpers dla kolorów
  const getCoreColor = (name) => {
    if (!name) return '#b6b6b6';
    const lowerName = name.toLowerCase();
    if (lowerName.includes('sio2')) return '#e2d587';
    if (lowerName.includes('tio2')) return '#949494';
    if (lowerName.includes('zno')) return '#f55353';
    if (lowerName.includes('ag')) return '#c0c0c0';
    return '#b6b6b6';
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const coreName = endpointDetails?.endpoint_name || 'Nanoform';
  const coreColor = getCoreColor(coreName);

  return (
    <div className='lab-container'>
      <div className='new-pred-container'>

        {/* --- NAVIGATION TABS --- */}
        <div className='np-tabs'>
          <button onClick={closeSelectModels} className='x-but'>
            <svg viewBox="0 0 24 24" focusable="false" className='icon-x' aria-hidden="true"><path fill="currentColor" d="M.439,21.44a1.5,1.5,0,0,0,2.122,2.121L11.823,14.3a.25.25,0,0,1,.354,0l9.262,9.263a1.5,1.5,0,1,0,2.122-2.121L14.3,12.177a.25.25,0,0,1,0-.354l9.263-9.262A1.5,1.5,0,0,0,21.439.44L12.177,9.7a.25.25,0,0,1-.354,0L2.561.44A1.5,1.5,0,0,0,.439,2.561L9.7,11.823a.25.25,0,0,1,0,.354Z"></path></svg>
          </button>
          <div className='np-steps'>
            <p className='p-step'>STEP 1</p>
            <p className='mar-btm-few previous-step'>Select substance</p>
            <div className="step-progress"></div>
          </div>
          <div className='np-steps'>
            <p className='p-step'>STEP 2</p>
            <p className='mar-btm-few previous-step'>Select endpoints</p>
            <div className="step-progress"></div>
          </div>
          <div className='np-steps active-tab-cm'>
            <p className='p-step'>STEP 3</p>
            <p className='mar-btm-few'>Define nanoform</p>
            <div className="step-progress"></div>
          </div>
        </div>

        <div className='new-study-wizard-body'>
          <div className='new-study-wizard-configurator'>
            <div className="define-nanoform">

              {/* --- ERROR MESSAGE --- */}
              {error && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#ffe6e6', border: '1px solid #ff0000', borderRadius: '8px' }}>
                  <p style={{ color: '#ff0000', margin: '0', fontWeight: 'bold' }}>{error}</p>
                </div>
              )}

              {/* --- CRYSTAL STRUCTURE --- */}
              {modelDescriptors?.crystal_structure?.available_structures?.length > 0 && (
                <div className="shape-section">
                  <h2 className="main-title">Crystal structure</h2>
                  <div className="custom-dropdown-container">
                    <button
                      type="button"
                      className={`menu-button-trigger-white ${isSubmitted && !selectedCrystalStructure ? 'error-input' : ''}`}
                      onClick={() => setIsCrystalOpen(!isCrystalOpen)}
                      style={isSubmitted && !selectedCrystalStructure ? { border: '2px solid red' } : {}}
                    >
                      <span>{selectedCrystalStructure || "Select Structure"}</span>
                      <svg className={`chevron-svg ${isCrystalOpen ? 'rotated' : ''}`} viewBox="0 0 512 512" width="14">
                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M112 184l144 144 144-144" />
                      </svg>
                    </button>

                    {isCrystalOpen && (
                      <div className="menu-list-dropdown">
                        {modelDescriptors.crystal_structure.available_structures.map((opt, i) => (
                          <button
                            key={i}
                            type="button"
                            className="menu-item-option"
                            onClick={() => { setSelectedCrystalStructure(opt); setIsCrystalOpen(false); }}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* --- CORE MODIFICATION --- */}
              <div className="shape-section" style={{ marginTop: '24px' }}>
                <h2 className="main-title">Core modification</h2>
                <div className="custom-dropdown-container">
                  <button
                    type="button"
                    className="menu-button-trigger-white"
                    onClick={() => setIsCoreOpen(!isCoreOpen)}
                  >
                    <span>{selectedCore}</span>
                    <svg className={`chevron-svg ${isCoreOpen ? 'rotated' : ''}`} viewBox="0 0 512 512" width="14">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M112 184l144 144 144-144" />
                    </svg>
                  </button>

                  {isCoreOpen && (
                    <div className="menu-list-dropdown">
                      {coreOptions.map((opt, i) => (
                        <button
                          key={i}
                          type="button"
                          className="menu-item-option"
                          onClick={() => { setSelectedCore(opt); setIsCoreOpen(false); }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* --- SURFACE FUNCTIONALIZATION --- */}
              <div className="shape-section" style={{ marginTop: '24px' }}>
                <h2 className="main-title">Surface functionalization</h2>
                <div className="custom-dropdown-container">
                  <button
                    type="button"
                    className="menu-button-trigger-white"
                    onClick={() => setIsSurfaceOpen(!isSurfaceOpen)}
                  >
                    <span>{selectedSurface}</span>
                    <svg className={`chevron-svg ${isSurfaceOpen ? 'rotated' : ''}`} viewBox="0 0 512 512" width="14">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M112 184l144 144 144-144" />
                    </svg>
                  </button>

                  {isSurfaceOpen && (
                    <div className="menu-list-dropdown">
                      {surfaceOptions.map((opt, i) => (
                        <button
                          key={i}
                          type="button"
                          className="menu-item-option"
                          onClick={() => {
                            setSelectedSurface(opt);
                            setIsSurfaceOpen(false);
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* --- MAIN FORM --- */}
              <form className="define-nanoform-form" onSubmit={handleSubmit}>

                {/* --- SHAPE SELECTION --- */}
                <div role='group' className="shape-section">
                  <h2 className="main-title">Shape</h2>

                  <div role='radiogroup' className='shape-radiogroup'>
                    <h3 className="shape-label">SPHEROIDAL</h3>
                    <div className="shape-group">
                      {renderShapeButton('spherical', 'Spherical')}
                      {renderShapeButton('cubic', 'Cubic')}
                      {renderShapeButton('polyhedral', 'Polyhedral')}
                    </div>

                    <h3 className="shape-label">ELONGATED</h3>
                    <div className="shape-group">
                      {renderShapeButton('rod', 'Rod')}
                      {renderShapeButton('tube', 'Tube')}
                      {renderShapeButton('wires', 'Wire')}
                    </div>

                    <h3 className="shape-label">PLATELET</h3>
                    <div className="shape-group">
                      {renderShapeButton('platelet', 'Platelet')}
                    </div>

                    <h3 className="shape-label">OTHER</h3>
                    <div className="shape-group">
                      {renderShapeButton('inconclusive', 'Inconclusive')}
                    </div>
                  </div>
                </div>

                {/* --- DYNAMIC DESCRIPTOR FIELDS --- */}
                {modelDescriptors?.descriptors && modelDescriptors.descriptors.length > 0 ? (
                  <>
                    <h2 className="main-title" style={{ marginTop: '30px', marginBottom: '15px' }}>Required Descriptors</h2>
                    {modelDescriptors.descriptors.map((descriptor, index) => {
                      const frontendKey = modelDescriptors.descriptors2frontend[index];
                      const unit = modelDescriptors.descriptors_units[index];
                      const value = descriptorValues[frontendKey] || '';
                      const isMeanSize = descriptor.toLowerCase().includes('mean');

                      const hasError = isSubmitted && (!value || value.trim() === '' || isNaN(parseFloat(value)) || parseFloat(value) < 0);

                      return (
                        <div key={frontendKey} role='group' className="shape-section" style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                            {descriptor} {unit ? `[${unit}]` : ''}
                          </label>
                          <input
                            className="nqtb-input"
                            type="number"
                            step="any"
                            min="0"
                            value={value}
                            placeholder={`e.g. 100.50`}
                            style={hasError ? { border: '2px solid red' } : {}}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              handleDescriptorChange(frontendKey, newVal);

                              // Update visualization scale if it's mean particle size
                              if (isMeanSize) {
                                setMeanParticleSize(newVal);
                              }
                            }}
                            required
                          />
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <p style={{ color: '#666', fontStyle: 'italic', marginTop: '20px' }}>
                    No specific descriptors are required for the selected models.
                  </p>
                )}
              </form>

              {/* --- NAVIGATION BUTTONS --- */}
              <div className="next-btn-wrapper">
                <div className="next-btn-wrapper-inside">
                  <button onClick={handleBack} type="button" className="back-button">Back</button>
                  <button onClick={handleSubmit} type="button" className='next-button'>
                    Run Prediction
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* --- VISUALIZATION AREA --- */}
          <div className='new-study-wizard-visualization'>
            <Visualization
              core={{ name: coreName }}
              coreModification={selectedCore !== 'No modification' ? selectedCore : null}
              coreColor={coreColor}
              surfaceFunctionalization={selectedSurface !== 'No modification'}
              surfaceFunctionalizationType={selectedSurface !== 'No modification' ? selectedSurface : null}
              surfaceFunctionalizationColor='#718B9F'
              lineColor="#7fb13a"
              isNanoformImageGenerated={false}
              visualizationType={visualizationType}
              measureScale={parseFloat(meanParticleSize) || 0}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefineNanoform;
